/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState } from 'react';
import { Building2, Users, ArrowRightLeft, Check, RefreshCw, Pencil, X, Save } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog';
import type { UserOrganizationWithDetails, OrganizationMember } from '@shared/schema/organizations';
import { USER_ORG_ROLES } from '@shared/schema/organizations';

function getRoleDisplayName(role: string): string {
  const roleNames: Record<string, string> = {
    'owner': 'Owner',
    'admin': 'Admin',
    'member': 'Member',
    'viewer': 'Viewer',
  };
  return roleNames[role] || role;
}

function getRoleBadgeStyle(role: string): React.CSSProperties {
  const styles: Record<string, React.CSSProperties> = {
    'owner': { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' },
    'admin': { background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' },
    'member': { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' },
    'viewer': { background: '#f9fafb', color: '#6b7280', border: '1px solid #e5e7eb' },
  };
  return styles[role] || styles['member'];
}

interface OrganizationInfoSectionProps {
  userOrgs: UserOrganizationWithDetails[];
  defaultOrg: UserOrganizationWithDetails | undefined;
  orgMembers: OrganizationMember[];
  isLoadingOrgs: boolean;
  isLoadingMembers: boolean;
  isChangingOrg: boolean;
  onChangeOrganization: (organizationId: string) => void;
  onUpdateOrganizationName?: (name: string) => void;
  isUpdatingName?: boolean;
}

export function OrganizationInfoSection({
  userOrgs,
  defaultOrg,
  orgMembers,
  isLoadingOrgs,
  isLoadingMembers,
  isChangingOrg,
  onChangeOrganization,
  onUpdateOrganizationName,
  isUpdatingName = false,
}: OrganizationInfoSectionProps) {
  const [isChangeOrgModalOpen, setIsChangeOrgModalOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const hasMultipleOrgs = userOrgs.length > 1;
  
  const isOwner = defaultOrg?.role === USER_ORG_ROLES.OWNER;
  
  const handleStartEditing = () => {
    setEditedName(defaultOrg?.organization?.name || '');
    setIsEditingName(true);
  };
  
  const handleCancelEditing = () => {
    setIsEditingName(false);
    setEditedName('');
  };
  
  const handleSaveName = () => {
    if (editedName.trim() && onUpdateOrganizationName) {
      onUpdateOrganizationName(editedName.trim());
      setIsEditingName(false);
    }
  };

  const handleOrgChange = (organizationId: string) => {
    onChangeOrganization(organizationId);
    setIsChangeOrgModalOpen(false);
  };

  return (
    <>
      {/* Current Organization Info */}
      <div style={{ 
        marginBottom: '32px',
        padding: '20px',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '12px',
        border: '1px solid #bae6fd'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Building2 size={24} style={{ marginRight: '12px', color: '#0369a1', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              {isEditingName ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') handleCancelEditing();
                    }}
                    autoFocus
                    style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#0c4a6e',
                      padding: '4px 8px',
                      border: '2px solid #0ea5e9',
                      borderRadius: '6px',
                      outline: 'none',
                      flex: 1,
                      maxWidth: '300px',
                    }}
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={!editedName.trim() || isUpdatingName}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '6px',
                      background: '#0ea5e9',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      cursor: !editedName.trim() || isUpdatingName ? 'not-allowed' : 'pointer',
                      opacity: !editedName.trim() || isUpdatingName ? 0.6 : 1,
                    }}
                    title="Save"
                  >
                    {isUpdatingName ? <RefreshCw size={16} className="spin" /> : <Save size={16} />}
                  </button>
                  <button
                    onClick={handleCancelEditing}
                    disabled={isUpdatingName}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '6px',
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      color: '#6b7280',
                      cursor: isUpdatingName ? 'not-allowed' : 'pointer',
                    }}
                    title="Cancel"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#0c4a6e' }}>
                    {isLoadingOrgs ? 'Loading...' : (defaultOrg?.organization?.name || 'Your Organization')}
                  </h3>
                  {isOwner && onUpdateOrganizationName && (
                    <button
                      onClick={handleStartEditing}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '4px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '4px',
                        color: '#0369a1',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = '#e0f2fe';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                      title="Edit organization name"
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                </div>
              )}
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#0369a1' }}>
                Current workspace
              </p>
            </div>
          </div>
          {hasMultipleOrgs && !isEditingName && (
            <button
              onClick={() => setIsChangeOrgModalOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                background: '#fff',
                border: '1px solid #0ea5e9',
                borderRadius: '6px',
                color: '#0369a1',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#f0f9ff';
                e.currentTarget.style.borderColor = '#0284c7';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.borderColor = '#0ea5e9';
              }}
            >
              <ArrowRightLeft size={14} />
              Change Organization
            </button>
          )}
        </div>
        
        {/* Members List */}
        <div style={{ 
          background: '#fff',
          borderRadius: '8px',
          border: '1px solid #e0f2fe',
          overflow: 'hidden'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            padding: '12px 16px',
            borderBottom: '1px solid #e0f2fe',
            background: '#f0f9ff'
          }}>
            <Users size={16} style={{ marginRight: '8px', color: '#0369a1' }} />
            <span style={{ fontSize: '14px', fontWeight: 500, color: '#0c4a6e' }}>
              Team Members ({orgMembers.length})
            </span>
          </div>
          <div style={{ 
            maxHeight: '200px', 
            overflowY: 'auto',
            padding: '8px'
          }}>
            {isLoadingMembers ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>
                <RefreshCw size={16} className="spin" style={{ display: 'inline-block', marginRight: '8px' }} />
                Loading members...
              </div>
            ) : orgMembers.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                No members found
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {orgMembers.map((member) => (
                  <div 
                    key={member.userId}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      background: '#fafafa'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: '#e0f2fe',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#0369a1',
                        fontSize: '13px',
                        fontWeight: 500
                      }}>
                        {member.user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937' }}>
                          {member.user?.name || 'Unknown'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {member.user?.email || ''}
                        </div>
                      </div>
                    </div>
                    <span style={{
                      ...getRoleBadgeStyle(member.role),
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.025em'
                    }}>
                      {getRoleDisplayName(member.role)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Change Organization Modal */}
      <Dialog open={isChangeOrgModalOpen} onOpenChange={setIsChangeOrgModalOpen}>
        <DialogContent style={{ maxWidth: '420px' }}>
          <DialogHeader>
            <DialogTitle>Switch Organization</DialogTitle>
            <DialogDescription>
              Select an organization to set as your default workspace.
            </DialogDescription>
          </DialogHeader>
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {userOrgs.map((org) => (
              <button
                key={org.organizationId}
                onClick={() => {
                  if (!org.isDefault) {
                    handleOrgChange(org.organizationId);
                  }
                }}
                disabled={org.isDefault || isChangingOrg}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 16px',
                  background: org.isDefault ? '#f0f9ff' : '#fff',
                  border: org.isDefault ? '2px solid #0ea5e9' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: org.isDefault ? 'default' : 'pointer',
                  opacity: isChangingOrg ? 0.7 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  if (!org.isDefault && !isChangingOrg) {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }
                }}
                onMouseOut={(e) => {
                  if (!org.isDefault) {
                    e.currentTarget.style.background = '#fff';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Building2 size={20} style={{ color: org.isDefault ? '#0369a1' : '#6b7280' }} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: 500, 
                      color: org.isDefault ? '#0c4a6e' : '#1f2937'
                    }}>
                      {org.organization?.name || 'Unknown Organization'}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#6b7280',
                      marginTop: '2px'
                    }}>
                      {getRoleDisplayName(org.role)}
                    </div>
                  </div>
                </div>
                {org.isDefault && (
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    background: '#0ea5e9',
                    color: '#fff',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 500
                  }}>
                    <Check size={12} />
                    Current
                  </span>
                )}
              </button>
            ))}
          </div>
          {isChangingOrg && (
            <div style={{ 
              marginTop: '12px', 
              padding: '12px', 
              background: '#f0f9ff', 
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#0369a1',
              fontSize: '13px'
            }}>
              <RefreshCw size={14} className="spin" />
              Switching organization...
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
